import reporter from 'utils/reporter';

import type {
  BaseAPIData,
  CustomProductTemplateAPIData,
  CustomSizeProductCustomOptions,
  ProductCustomOptionsAPIData,
  ProductTemplateAPIData,
  RawCatalogAPIData,
  RawProductTemplateAPIData,
  RawSubcatalogAPIData,
} from 'types/catalog_types';
import { Dimension } from 'types/common_types';
import { orientationAndRatioPercentage, realNumber } from 'utils/math_util';

function flattenProductsFromCatalogs(catalogs: RawCatalogAPIData[]): ProductTemplateAPIData[] {
  return catalogs
    .flatMap((c) => (c.templates ?? []).flatMap((s) => s.templates))
    .map((p) => formatProductData(p));
}

function flattenProductsFromSubcatalogs(subcatalogs: RawSubcatalogAPIData[]) {
  return subcatalogs.flatMap((s) => s.templates).map(formatProductData);
}

function formatProductData(product: RawProductTemplateAPIData): ProductTemplateAPIData {
  let customOptions = null;

  if (product.custom_options && typeof product.custom_options === 'string') {
    try {
      customOptions = JSON.parse(product.custom_options);
    } catch (error) {
      reporter.addBreadcrumb({
        level: 'info',
        message: 'Failed to parse this custom option JSON',
        data: { json: product.custom_options },
      });

      reporter.captureException(error);
    }
  }

  // Merge the "product template" and "product" as the distinction is not very different
  // anymore. The name however is different, the product.product.name matching the
  // product.name_long, so we don't want to overwrite that value
  return { ...product, ...product.product, name: product.name, custom_options: customOptions };
}

function isCustomSizeProduct(p: ProductTemplateAPIData): p is CustomProductTemplateAPIData {
  if (!p.custom_options) return false;

  const ex = ['related'];
  return Object.keys(p.custom_options).filter((k) => ex.indexOf(k) === -1).length > 0;
}

/**
 * Calculates a product's base width and height
 */
function calculateProductSize(
  product: ProductTemplateAPIData,
  { height, width, rotation }: { height?: number; width?: number; rotation?: number },
  ignoreRotation?: boolean
) {
  if (isCustomSizeProduct(product)) {
    const w = width || 0;
    const h = height || 0;
    return {
      ...buildCombinedCustomSize(w, h, product.custom_options as CustomSizeProductCustomOptions),
    };
  }

  const { print_size_x, print_size_y } = product;
  const printX = parseFloat(print_size_x);
  const printY = parseFloat(print_size_y);

  if (ignoreRotation) {
    return {
      width: printX,
      height: printY,
    };
  }

  return {
    width: !rotation || rotation === 0 ? printX : printY,
    height: !rotation || rotation === 0 ? printY : printX,
  };
}

function findDefaultTemplateId<T extends BaseAPIData>(arr: T[]) {
  const found = arr.find((c) => !!c.default) || arr.find((c) => !c.read_only);

  return found ? { id: found.id, item: found } : {};
}

function getProductFilters(product: ProductTemplateAPIData | RawProductTemplateAPIData) {
  return product.filter_group
    ? product.filter_group
        .trim()
        .split(',')
        .map((s) => s.trim())
    : [];
}

function getProductIdFromDimensions(
  width: string | number,
  height: string | number,
  products: ProductTemplateAPIData[]
) {
  const w = typeof width === 'string' ? parseFloat(width) : width;
  const h = typeof height === 'string' ? parseFloat(height) : height;

  // get standard sizes
  const std = getStandardSizes(products);
  const standardMatch = std.find((s) => s.w === w && s.h === h);
  return standardMatch ? standardMatch.id : null;
}

function getStandardSizes(products: ProductTemplateAPIData[]) {
  return products
    .filter((p) => !p.custom_options)
    .map((s) => {
      const { id, name } = s;
      const [w, h] = name.split('x').map((v) => parseFloat(v));
      return { id, w, h };
    });
}

function productIsMultiNode(product: ProductTemplateAPIData) {
  return product.nodes.filter((n) => n.node_type === 1).length > 1;
}

function canRotateProduct(product: ProductTemplateAPIData) {
  const widthNum = parseFloat(product.print_size_x);
  const heightNum = parseFloat(product.print_size_y);
  const { orientation } = orientationAndRatioPercentage(widthNum, heightNum);

  return (
    product.rotation_allowed &&
    orientation !== 'square' &&
    !isCustomSizeProduct(product) &&
    !productIsMultiNode(product)
  );
}

// Extracts & calculates the bleed, frame, and wrap sizes for a whole product
function getBleedFrameWrapOptions(customOptions: ProductTemplateAPIData['custom_options']) {
  let { bleed, frame, wrap } = customOptions ?? { bleed: 0, frame: 0, wrap: 0 };

  // multiply by 2 to account for both sides
  bleed = (bleed && realNumber(bleed) && bleed * 2) || 0;
  frame = (frame && realNumber(frame) && frame * 2) || 0;
  wrap = (wrap && realNumber(wrap) && wrap * 2) || 0;

  return { bleed, frame, wrap };
}

// Calculates a custom size product's width and height with it's custom options
function buildCombinedCustomSize(
  width: number,
  height: number,
  customOptions: ProductCustomOptionsAPIData
) {
  const { bleed, frame, wrap } = getBleedFrameWrapOptions(customOptions);

  return {
    height: height + bleed + frame + wrap,
    width: width + bleed + frame + wrap,
  };
}

/** Check if this is a valid custom size */
function validateCustomSize(
  width: number,
  height: number,
  options: CustomSizeProductCustomOptions
) {
  const { shortmin, shortmax, longmin, longmax, increment } = options;

  if ((width / increment) % 1 !== 0 || (height / increment) % 1 !== 0) {
    return { valid: false, reason: 'Invalid Increment' };
  }

  const { orientation } = orientationAndRatioPercentage(width, height);

  let short: number;
  let long: number;

  switch (orientation) {
    case 'square':
    case 'portrait':
      short = width;
      long = height;
      break;
    case 'landscape':
      short = height;
      long = width;
      break;
  }

  const valid = short >= shortmin && short <= shortmax && long >= longmin && long <= longmax;
  const reason = valid ? undefined : 'Invalid Size';

  return {
    valid,
    reason,
  };
}

/**
 * Print sizes are not really reflective of a product's understood dimensions, and typically the bounds
 * of the image node are the closest representation of what the user will think is the product's dimension
 */
function getProductRealDimension(product: ProductTemplateAPIData): Dimension {
  const xywh = product.nodes.find((n) => n.node_type === 1)?.bounds.split(',');

  if (isCustomSizeProduct(product) || !xywh) {
    return {
      height: parseInt(product.print_size_y),
      width: parseInt(product.print_size_x),
    };
  }

  const [, , w, h] = xywh;

  return {
    height: parseInt(h),
    width: parseInt(w),
  };
}

export default {
  flattenProductsFromCatalogs,
  flattenProductsFromSubcatalogs,
  formatProductData,
  calculateProductSize,
  isCustomSizeProduct,
  productIsMultiNode,
  canRotateProduct,
  getProductRealDimension,
  validateCustomSize,
  getProductFilters,
  findDefaultTemplateId,
  buildCombinedCustomSize,
  getBleedFrameWrapOptions,
  getProductIdFromDimensions,
};
